import type { pt } from './pt'

type Translation<T> = { [K in keyof T]: T[K] extends string ? string : Translation<T[K]> }

export const en: Translation<typeof pt> = {
  app: {
    abaRadio: 'Radio',
    abaGente: 'People',
    abaEstacao: 'Station',
    abaProgresso: 'Progress',
    abaAdmin: 'Admin',
    secoes: 'Sections',
    ouvinte: 'LISTENER',
    classe: 'CLASS',
    daProva: 'from the exam',
    apelido: 'nickname',
    conectado: 'connected',
    desconectado: 'disconnected',
    sair: 'log out',
    audioEstado: 'audio',
    audioIniciando: 'starting…',
    tentarNovamente: 'try again',
    sinalEncontradoEm: 'Signal found at {freq} MHz.',
    varreduraCompleta: 'Scan complete: nothing found on this band.',
    noCanal: 'On the channel',
    ninguem: 'nobody',
    noAr: 'ON AIR',
    canalVazioPre: 'Nobody on this frequency. Check',
    canalVazioPos: 'for where things are happening, or plan a meetup.',
    ocultarMapa: 'Hide map',
    mapaDePropagacao: 'Propagation map',
  },
  login: {
    titulo: 'Radioamador',
    confereEmail: 'Check your email',
    modoDev: 'Development mode',
    modoDevTexto:
      'This server does not send email, so the link came back in the API response. In production this is an open login and cannot exist.',
    entrarComLink: 'Log in with the link',
    usarOutroEmail: 'use another email',
    erroEnviarLink: 'could not send the link',
    linkInvalido: 'invalid link',
    linkEnviado: 'Link sent.',
    cadastroIntro:
      'Create your account with email and password — no need to open your email right now. The email is only actually used if you forget your password later.',
    comoChamar: 'What should we call you',
    apelidoPlaceholder: 'YOUR NICKNAME',
    cadastroHintPre: '3 to 12 letters or numbers.',
    cadastroHintForte: "You don't need a callsign",
    cadastroHintPos:
      'to join: with a nickname you can already talk on the Citizens Band (PX) and on the open services. A ham radio callsign comes later — by passing our exam, or by proving the license you already hold with Anatel.',
    email: 'Email',
    emailPlaceholder: 'you@example.com',
    senha: 'Password',
    confirmarSenha: 'Confirm password',
    criarConta: 'Create account',
    criando: 'creating…',
    jaTenhoConta: 'I already have an account',
    senhasNaoConferem: "passwords don't match",
    erroCriarConta: 'could not create the account',
    erroServidor: 'could not reach the server',
    recuperarIntro:
      'Forgot your password? Enter your registered email — we will send you a link to log in and change it.',
    receberLink: 'Get access link',
    enviando: 'sending…',
    jaTenhoSenha: 'I already have a password',
    loginLabel: 'Callsign, nickname or email',
    entrar: 'Log in',
    entrando: 'logging in…',
    naoTemConta: "Don't have an account? Sign up",
    esqueciSenha: 'Forgot password',
    erroEntrar: 'could not log in',
    desafioInstrucao: "Drag until you tune the mark — that's how we know it's you",
    desafioSintonizado: 'Tuned ✓',
    desafioCarregando: 'preparing the dial…',
    erroDesafio: "couldn't prepare the check — reload the page",
  },
  admin: {
    carregando: 'loading…',
    pedidosTitulo: 'Callsign requests',
    semPedidos: 'No pending requests.',
    classe: 'class',
    aprovar: 'approve',
    recusar: 'reject',
    verDocumento: 'view document',
    comDocumento: 'with document',
    semDocumento: 'without document',
    pedidosHint:
      'Approving adopts the callsign on the account and marks the license as verified. Documentation arrives by email and is not stored here.',
    contasTitulo: 'Accounts',
    admin: 'ADMIN',
    loginBloqueado: 'LOGIN BLOCKED',
    txBloqueado: 'TX BLOCKED',
    ouvinte: 'listener',
    comSenha: 'with password',
    soLink: 'link only',
    voce: 'you',
    administra: 'administers',
    editar: 'edit',
    excluir: 'delete',
    editarTitulo: 'Edit',
    swl: 'SWL (listener)',
    classeC: 'Class C',
    classeB: 'Class B',
    classeA: 'Class A',
    indicativoApelido: 'Callsign/nickname',
    email: 'Email',
    redefinirSenha: 'Reset password (leave blank to keep it unchanged)',
    bloquearLogin: 'Block login — the person can no longer log in, at all',
    bloquearTx: 'Block transmit — still logs in and listens, just cannot transmit',
    edicaoHint:
      'If the person is currently connected, the effect is immediate — the connection drops and they reconnect on their own with the new state.',
    salvar: 'Save',
    cancelar: 'cancel',
    excluirTitulo: 'Delete',
    excluirAviso:
      'Everything goes with it, with no undo: logbook, credits, purchases, station profile, meetups and callsign requests. The session stops being valid instantly.',
    digitePara: 'Type',
    paraConfirmar: 'to confirm',
    excluirDefinitivamente: 'Delete permanently',
    erroListar: 'could not list the accounts',
    erroExcluir: 'could not delete',
    excluida: 'was deleted, along with everything tied to it.',
    erroServidor: 'could not reach the server',
    atualizada: 'was updated.',
    erroSalvar: 'could not save',
  },
  awards: {
    titulo: 'Awards',
    resumoIniciados: '{iniciados} of {total} started',
    faltamContagem: '{n} missing',
    faltamLista: 'Missing: {lista}',
    eMaisContagem: 'and {n} more',
    completo: 'Complete',
    proximoTier: 'Next: {tier} — {n} to go',
    feitosLista: 'Done: {lista}',
    nota:
      'Clear Mode and imported QSOs go into the history, but do not count toward awards — otherwise the trophy would mean nothing.',
  },
  cwSender: {
    modoSimples: 'Simple',
    modoAvancado: 'Advanced',
    modoChave: 'Manual key',
    transmitindoSolte: 'Transmitting — release to finish the dot or dash',
    segureParaTransmitir: 'Hold to transmit',
    noAr: 'ON AIR',
    chave: 'KEY',
    velocidade: 'Speed:',
    palavrasPorMinuto: 'words per minute — tap to change',
    finalizar: 'Finish',
    parar: 'Stop',
    placeholderAvancado: 'type — every letter goes out live',
    placeholderSimples: 'CQ CQ DE ...',
    enviar: 'Send',
    textoParaCW: 'Text to send in CW',
    jeitoDeMandar: 'Sending mode:',
    toqueParaTrocar: '— tap to change',
    semPrivilegio: 'no privilege on this frequency',
    alfabetoMorse: 'Morse Alphabet',
    enviado: 'Sent:',
  },
  dock: {
    filtroLargo: 'Wide',
    filtroNormal: 'Normal',
    filtroEstreito: 'Narrow',
    filtroCW: 'CW',
    squelchDesligado: 'Off',
    squelchBaixo: 'Low',
    squelchMedio: 'Medium',
    squelchAlto: 'High',
    descer5: 'Down 5 kHz',
    descerMeio: 'Down half kHz',
    subirMeio: 'Up half kHz',
    subir5: 'Up 5 kHz',
    bandasAriaLabel: 'Bands',
    canal: 'CHANNEL',
    modoLabel: 'Mode:',
    toqueParaTrocar: '— tap to change',
    filtroFI: 'IF filter:',
    squelchLabel: 'Squelch:',
    sql: 'SQL',
    chiadoLigado: 'Band noise on — tap to turn it off and hear only voice',
    audioLimpo: 'Clean audio, no noise — tap to turn band noise back on',
    chiado: 'Noise',
    limpo: 'Clean',
    parar: 'Stop',
    varrer: 'Scan',
    semPrivilegio: 'no privilege on this frequency',
    canalSilencioso: 'quiet channel',
    sinalFraco: 'weak signal — antenna, power or distance may be limiting it. Adjust it in Station.',
    escalaFrequenciaAria: 'Frequency scale. Drag to tune.',
  },
  exam: {
    titulo: 'Class C Exam',
    tecnicaEtica: 'Technique and ethics',
    legislacao: 'Regulations',
    eletronica: 'Electronics',
    examRule: 'Passing requires {n}% in EACH discipline, not the average.',
    oQueVoceErrou: 'What you got wrong',
    certa: 'Correct:',
    fazerOutraProva: 'Take another exam',
    anterior: 'Previous',
    proxima: 'Next',
    entregarProva: 'Submit exam',
    respondidasContagem: '{respondidas} of {total} answered',
    introPre: 'Three disciplines, 15 questions each, modeled on the COER. Passing requires getting 8 of 15 right in',
    introForte: 'each',
    introPos: 'discipline — not the average. No telegraphy test: Res. 777/2025 abolished mandatory CW.',
    iniciarProva: 'Start exam',
    erroIniciar: 'could not start the exam',
  },
  gente: {
    agora: 'NOW',
    ja: 'now',
    emMinutos: 'in {n} min',
    emHoras: 'in {n} h',
    emDia: 'in {n} day',
    emDias: 'in {n} days',
    noArAgora: 'On air now',
    vazio:
      'Nobody on air right now. Plan a meetup below — that is how the hobby has always solved this: agree on a time and frequency, and the two people show up.',
    noAr: 'on air',
    noArPill: 'ON AIR',
    encontrosMarcados: 'Scheduled meetups',
    cancelar: 'cancel',
    marcar: 'schedule',
    vazioEncontros:
      'Nothing scheduled yet. Scheduling a meetup is the most direct way to turn "is anyone there?" into a conversation.',
    convida: 'invites ·',
    ninguemConfirmou: 'nobody has confirmed yet',
    confirmados: 'confirmed',
    naoVouMais: "I'm not going anymore",
    euVou: "I'm going",
    desmarcar: 'cancel',
    sobreOQue: 'About what',
    placeholderTitulo: 'Saturday chat',
    frequencia: 'Frequency (kHz)',
    modo: 'Mode',
    quando: 'When',
    recado: 'Note (optional)',
    placeholderRecado: 'Newcomers welcome — we help out',
    marcarEncontro: 'Schedule meetup',
    marcando: 'scheduling…',
    erroMarcar: 'could not schedule',
  },
  identidade: {
    minhaIdentidade: 'My identity',
    apelido: 'Nickname',
    indicativoDaProva: 'Exam callsign',
    indicativoOficial: 'Official callsign',
    hintLivrePre: 'With a nickname you can already talk on the',
    hintLivreForte: 'Citizens Band (PX)',
    hintLivrePos:
      'and the open services — it requires no callsign or class. For ham radio bands there are two paths: pass our exam, which issues a callsign right here, or prove the license you already hold.',
    hintSimulado:
      'This callsign was issued by us and only counts here: the prefix has three letters, a form the ITU never issues, so it never collides with a real ham radio callsign. On the bands, it counts the same as an official callsign of your class. If you already have your own callsign, you can reserve it below.',
    hintOficial:
      'Callsign verified against the license presented. To change class or callsign, open a new request.',
    trocarSenha: 'Change password',
    definirSenha: 'Set a password',
    senhaAtualPlaceholder: 'current password',
    novaSenhaPlaceholder: 'new password, 8 characters or more',
    senhaHint:
      'With a password you log in directly, without opening your email every time. The link still works — it is how you recover a forgotten password.',
    trocarSenhaBotao: 'Change password',
    definirSenhaBotao: 'Set password',
    removerSenha: 'remove the password and go back to logging in by link only',
    pedidoEmAnalise: 'Request under review for',
    pedidoClasseTemplate: '(class {classe})',
    pedidoMeio:
      'Nothing changes until someone reviews the document — you keep operating as you are now.',
    enviarComprovacaoTemplate: 'Send proof to {contato}.',
    documentoLabel: 'Document (photo or PDF)',
    documentoJaEnviado: 'Document already sent — sending another replaces it.',
    documentoAindaNao: 'No document sent yet.',
    enviarDocumentoBotao: 'Send document',
    enviandoDocumento: 'sending…',
    documentoEnviado: 'Document sent.',
    erroEnviarDocumento: 'could not send the document',
    indicativoOficialSeu: 'Official callsign that is yours',
    callsignPlaceholder: 'PY2ABC',
    reservarHint:
      'Reserving is for the license holder. We will ask for the license or COER proof with the active class, in your name.',
    classeAtiva: 'Active class',
    pedirReserva: 'Request callsign reservation',
    abrindo: 'opening…',
    naoGuardamos:
      'If you send a document, it is deleted automatically some time after the decision — it is not kept forever. Only the request and the result stay here.',
    erroAbrirPedido: 'could not open the request',
    erroServidor: 'could not reach the server',
    erroGravar: 'could not save',
    pronto: 'done',
    excluirContaToggle: 'Delete my account',
    excluirContaAviso:
      'Everything goes with it, with no undo: logbook, credits, purchases, station profile, meetups and callsign requests. You are logged out instantly.',
    digiteSeuIndicativo: 'Type',
    paraExcluirConta: 'to confirm',
    excluirContaBotao: 'Delete my account permanently',
    excluindoConta: 'deleting…',
    erroExcluirConta: 'could not delete the account',
  },
  instalarApp: {
    iosInstrucao: 'Install: tap Share and then "Add to Home Screen".',
    outroInstrucao: 'Install the app: full screen, no browser bar.',
    instalar: 'install',
    dispensar: 'dismiss',
  },
  logbook: {
    titulo: 'Logbook',
    qso: 'QSO',
    paises: 'countries',
    bandas: 'bands',
    pDiploma: 'for awards',
    indicativoPlaceholder: 'CALLSIGN',
    indicativoAria: 'Other station callsign',
    rstEnviado: 'RST sent',
    rstRecebido: 'RST received',
    gravar: 'Save',
    hintSufixo: 'band, country and distance are filled in by the server',
    jaHaContato: 'there is already a contact with this station today, on this band and mode',
    erroGravar: 'could not save',
    claro: 'CLEAR',
    exportarADIF: 'Export ADIF',
    exportarHint: 'exported marked as simulated — do not upload to LoTW or eQSL',
  },
  mapView: {
    mapaIndisponivel: 'map unavailable:',
    dia: 'day',
    noite: 'night',
    greyLineAgora: 'GREY LINE now',
    doSolEm: 'in',
    min: 'min',
  },
  ptt: {
    transmitindoSolte: 'Transmitting. Release to listen.',
    segureParaFalar: 'Hold to talk',
    bipe: 'BEEP',
    noAr: 'ON AIR',
    pttLabel: 'PTT',
    aguardeBipe: 'wait for the beep',
    desconectado: 'disconnected',
    semPrivilegio: 'no privilege here',
    toqueParaParar: 'tap to stop',
    solteParaOuvir: 'release to listen',
    toqueParaFalar: 'tap to talk',
    segureParaFalar2: 'hold to talk',
    toque: 'tap',
    segurar: 'hold',
    alternarTitle: 'Toggles between hold and tap to transmit',
  },
  shack: {
    titulo: 'Station and credits',
    creditos: 'credits',
    semSeguidas: 'wk. streak',
    creditosDisponiveis: 'credits available',
    deOndeVeio: 'where did it come from?',
    ocultarExtrato: 'hide statement',
    sequenciaAtualSingular: 'Current streak: 1 week in a row',
    sequenciaAtualPlural: 'Current streak: {n} weeks in a row',
    semSequenciaSingular: 'No streak right now — record: 1 week',
    semSequenciaPlural: 'No streak right now — record: {n} weeks',
    faltamParaBonus: '{n} to go for the next bonus',
    ledgerVazio: 'No credits yet. Make a QSO.',
    eSeu: 'is yours.',
    erroComprar: 'could not buy',
    rig: 'Transceivers',
    antenna: 'Antennas',
    feedline: 'Feedline (20 m roll)',
    amplifier: 'Amplifiers',
    rotator: 'Rotators',
    tower: 'Towers and masts',
    filter: 'IF filters',
    seu: 'YOURS',
    cr: 'cr',
    nota:
      'All equipment is earned by operating. Nothing that affects RF performance is sold for real money — an environment where whoever pays is heard better destroys the premise of the hobby.',
  },
  station: {
    minhaEstacao: 'My station',
    bipeRoger: 'Beep on PTT release (roger)',
    bipeRogerHint:
      'Plays after your last word, with the transmitter still on. It says "over, go ahead" — on FM and on the PX there is no audible carrier, so without it nobody knows if you finished or just took a breath.',
    bipeInicio: 'Beep on PTT press',
    bipeInicioHint:
      'Warns that your voice is about to start. The microphone only opens AFTER the tone — the PTT button shows when it is time to talk.',
    ouvir: 'listen',
    ligueAudioPrevia: 'Turn on audio in the Radio tab to hear the preview.',
    bipeHint:
      'The beep is transmitted together with the voice, so it travels the same radio path: a weak station has a weak beep.',
    transceptor: 'Transceiver',
    antena: 'Antenna',
    rotor: 'Rotator',
    cabo: 'Feedline',
    filtroFI: 'IF filter',
    nenhum: 'None',
    rotorHint: 'Prerequisite to equip a directional antenna (Yagi) — without a rotator, it stays locked.',
    precisaRotor:
      'The chosen antenna needs a rotator to be equipped. Buy one in the shop or switch to an antenna without that requirement.',
    filtroHint: 'Without an accessory, the bandwidth floor is 500 Hz (or whatever the radio already ships with).',
    alturaAntena: 'Antenna height (m)',
    alturaHint:
      'Up to {max} m with what you have today — without a tower, the ceiling is a roof mast. Height decides takeoff angle, not power.',
    torre: 'Tower',
    torreHint: 'Without a tower, the height ceiling is a roof mast.',
    direcaoAntena: 'Antenna heading (degrees, 0=North)',
    direcaoHint: 'Only matters for a directional antenna (Yagi) and for the dipole (the wire orientation).',
    comprimentoCabo: 'Feedline length (m)',
    caboHint: 'A feedline that is too long gives back in loss what the antenna gained.',
    potencia: 'Power (W)',
    potenciaHint: 'Up to {max} W with what you have today.',
    amplificador: 'Amplifier',
    amplificadorHint: 'Without an amplifier, the power ceiling is whatever the radio delivers on its own.',
    swr: 'SWR',
    swrHint: 'A bad match turns into loss in the feedline.',
    ambienteQTH: 'QTH environment',
    ambienteHint:
      'Sets the noise floor. Listening is harder than talking — and the environment weighs more than the radio in that equation.',
    locator: 'Locator (Maidenhead grid)',
    locatorHint:
      'E.g.: GG66oe. A 6-character grid is about a ~5×5 km area — enough for the propagation engine, without exposing your exact address.',
    qthHint: 'Changing QTH has a {h} h cooldown — otherwise chasing awards would be teleportation.',
    salvarEstacao: 'Save station',
    salvando: 'saving…',
    sucesso: 'Station is on air with the new configuration.',
    erroSalvar: 'could not save',
    erroServidor: 'could not reach the server',
  },
  waterfall: {
    semSinal: 'no signal',
    sinal: 'signal',
    sinais: 'signals',
    piso: 'floor',
    bandaSilenciosa: 'quiet band',
    zoom: 'Zoom:',
    toqueParaTrocar: '— tap to change',
    farol: 'BEACON',
  },
  langSelector: {
    idioma: 'Language',
  },
}
